import { useCallback, memo, useState, useEffect } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  Connection,
  addEdge,
  useReactFlow,
  Node,
  Edge,
  NodeMouseHandler,
  EdgeMouseHandler,
  reconnectEdge,
  SelectionMode
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toast } from 'sonner';
import { nodeTypes, generateUUID } from './utils/diagramUtils';
import { NodeData } from './initial-nodes';
import { ComponentSearchDropdown } from './ComponentSearchDropdown';

interface DiagramCanvasProps {
  nodes: Node<NodeData>[];
  edges: Edge[];
  onNodesChange: (changes: any) => void;
  onEdgesChange: (changes: any) => void;
  setNodes: React.Dispatch<React.SetStateAction<Node<NodeData>[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  onNodeContextMenu: NodeMouseHandler;
  onEdgeContextMenu: EdgeMouseHandler;
  onPaneClick: () => void;
  setReactFlowInstance: (instance: any) => void;
  subscriptionId?: string;
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onAddNew: () => void;
  selectedElements: { nodes: Node<NodeData>[]; edges: Edge[] };
  onElementsSelect: (nodes: Node<NodeData>[], edges: Edge[]) => void;
  onNodeClick: (event: React.MouseEvent, node: Node<NodeData>) => void;
  onEdgeClick: (event: React.MouseEvent, edge: Edge) => void;
  onNodeDoubleClick?: (event: React.MouseEvent, node: Node<NodeData>) => void;
  onEdgeDoubleClick?: (event: React.MouseEvent, edge: Edge) => void;
  autoSaveStatus?: { isSaving: boolean; lastSaved?: Date; error?: Error };
  diagramName?: string;
}

export const DiagramCanvas = memo(({ 
  nodes, 
  edges, 
  onNodesChange, 
  onEdgesChange, 
  setNodes,
  setEdges,
  onNodeContextMenu,
  onEdgeContextMenu,
  onPaneClick,
  setReactFlowInstance,
  subscriptionId,
  onSave,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onAddNew,
  selectedElements,
  onElementsSelect,
  onNodeClick: onNodeClickProp,
  onEdgeClick: onEdgeClickProp,
  onNodeDoubleClick: onNodeDoubleClickProp,
  onEdgeDoubleClick: onEdgeDoubleClickProp,
  autoSaveStatus,
  diagramName
}: DiagramCanvasProps) => {
  const reactFlowInstance = useReactFlow();
  const [selectedView, setSelectedView] = useState('visao-padrao');

  // Resetar visão para padrão sempre que nodes mudarem (carregamento de diagrama)
  useEffect(() => {
    if (nodes.length > 0) {
      setSelectedView('visao-padrao');
    }
  }, [nodes.length]);

  // Calcular gradiente baseado nos valores de codeChanges7Days
  const getCodeGradientValues = () => {
    const codeChangesValues = nodes
      .filter(node => node.data.codeChanges7Days !== undefined && node.data.codeChanges7Days !== null)
      .map(node => node.data.codeChanges7Days as number);
    
    if (codeChangesValues.length === 0) {
      return { min: 0, max: 0 };
    }

    return {
      min: Math.min(...codeChangesValues),
      max: Math.max(...codeChangesValues)
    };
  };

  // Calcular gradiente baseado nos valores de deploys7Days
  const getDeployGradientValues = () => {
    const deployValues = nodes
      .filter(node => node.data.deploys7Days !== undefined && node.data.deploys7Days !== null)
      .map(node => node.data.deploys7Days as number);
    
    if (deployValues.length === 0) {
      return { min: 0, max: 0 };
    }

    return {
      min: Math.min(...deployValues),
      max: Math.max(...deployValues)
    };
  };

  // Atualizar nodes com informações de gradiente
  useEffect(() => {
    if (selectedView === 'visao-codigo') {
      const { min, max } = getCodeGradientValues();
      
      setNodes(prevNodes => 
        prevNodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            viewMode: 'codigo',
            gradientMin: min,
            gradientMax: max
          }
        }))
      );
    } else if (selectedView === 'visao-deploy') {
      const { min, max } = getDeployGradientValues();
      
      setNodes(prevNodes => 
        prevNodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            viewMode: 'deploy',
            deployGradientMin: min,
            deployGradientMax: max
          }
        }))
      );
    } else {
      setNodes(prevNodes => 
        prevNodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            viewMode: 'padrao'
          }
        }))
      );
    }
  }, [selectedView, setNodes]);

  // Lidar com clique em nó
  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node<NodeData>) => {
    event.stopPropagation();
    onNodeClickProp(event, node);
  }, [onNodeClickProp]);

  // Lidar com duplo clique em nó
  const handleNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node<NodeData>) => {
    event.stopPropagation();
    if (onNodeDoubleClickProp) {
      onNodeDoubleClickProp(event, node);
    }
  }, [onNodeDoubleClickProp]);

  // Lidar com clique em edge
  const handleEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.stopPropagation();
    onEdgeClickProp(event, edge);
  }, [onEdgeClickProp]);

  // Lidar com duplo clique em edge
  const handleEdgeDoubleClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.stopPropagation();
    if (onEdgeDoubleClickProp) {
      onEdgeDoubleClickProp(event, edge);
    }
  }, [onEdgeDoubleClickProp]);

  // Lidar com clique no canvas (área branca)
  const handlePaneClick = useCallback((event: React.MouseEvent) => {
    onPaneClick();
  }, [onPaneClick]);

  // Handler para seleção por arrastar (drag box)
  const onSelectionChange = useCallback((params: any) => {
    onElementsSelect(params.nodes || [], params.edges || []);
  }, [onElementsSelect]);

  // Lidar com context menu no pane (background)
  const handlePaneContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges((eds) => addEdge(connection, eds));
    },
    [setEdges]
  );

  const onReconnect = useCallback(
    (oldEdge: Edge, newConnection: Connection) => {
      setEdges((els) => reconnectEdge(oldEdge, newConnection, els));
    },
    [setEdges]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      const componentId = event.dataTransfer.getData('application/componentId');
      const componentName = event.dataTransfer.getData('application/componentName');
      const componentTypeId = event.dataTransfer.getData('application/componentTypeId');
      const componentTypeName = event.dataTransfer.getData('application/componentTypeName');
      const componentIcon = event.dataTransfer.getData('application/componentIcon');
      const organizationId = event.dataTransfer.getData('application/organizationId');
      const organizationName = event.dataTransfer.getData('application/organizationName');
      const codeChanges7Days = event.dataTransfer.getData('application/codeChanges7Days');
      const codeChanges30Days = event.dataTransfer.getData('application/codeChanges30Days');
      const deploys7Days = event.dataTransfer.getData('application/deploys7Days');

      if (!type) {
        return;
      }

      const reactFlowBounds = document.querySelector('.react-flow')?.getBoundingClientRect();
      if (!reactFlowBounds) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const uuid = generateUUID();

      let newNode;

      if (type === 'labeledGroup') {
        newNode = {
          id: `group-${Date.now()}`,
          type: 'labeledGroup',
          position,
          data: { 
            label: componentName || 'Grupo',
            uuid: uuid
          },
          style: {
            width: 300,
            height: 200
          }
        };
      } else if (type === 'textNode') {
        newNode = {
          id: `text-${Date.now()}`,
          type: 'textNode',
          position,
          data: { 
            text: 'Texto',
            uuid: uuid
          },
        };
      } else if (type === 'actorNode') {
        newNode = {
          id: `actor-${Date.now()}`,
          type: 'actorNode',
          position,
          data: { 
            actorText: '',
            actorColor: '#6b7280',
            uuid: uuid
          },
        };
      } else if (type === 'componentNode') {
        newNode = {
          id: `component-${Date.now()}`,
          type: 'componentNode',
          position,
          data: { 
            name: componentTypeName,
            componentTypeId: componentTypeId,
            title: componentName,
            uuid: uuid,
            selectedComponentId: componentId,
            selectedComponentName: componentName,
            selectedOrganizationId: organizationId,
            selectedOrganizationName: organizationName,
            codeChanges7Days: parseInt(codeChanges7Days) || 0,
            codeChanges30Days: parseInt(codeChanges30Days) || 0,
            deploys7Days: parseInt(deploys7Days) || 0,
            icon: componentIcon
          },
        };
      } else {
        return;
      }

      setNodes((nds) => nds.concat(newNode));
      toast.success(`${componentName || 'Elemento'} adicionado com sucesso!`);
    },
    [reactFlowInstance, setNodes]
  );

  const onInit = useCallback((instance: any) => {
    setReactFlowInstance(instance);
  }, [setReactFlowInstance]);

  // Atualizar nodes e edges com informação de seleção
  const nodesWithSelection = nodes.map(node => ({
    ...node,
    selected: selectedElements.nodes.some(n => n.id === node.id)
  }));

  const edgesWithSelection = edges.map(edge => ({
    ...edge,
    selected: selectedElements.edges.some(e => e.id === edge.id)
  }));

  return (
    <div className="relative w-full h-full">
      <ReactFlow
        nodes={nodesWithSelection}
        edges={edgesWithSelection}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onReconnect={onReconnect}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onNodeContextMenu={onNodeContextMenu}
        onEdgeContextMenu={onEdgeContextMenu}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onEdgeClick={handleEdgeClick}
        onEdgeDoubleClick={handleEdgeDoubleClick}
        onPaneClick={handlePaneClick}
        onPaneContextMenu={handlePaneContextMenu}
        onSelectionChange={onSelectionChange}
        nodeTypes={nodeTypes}
        onInit={onInit}
        fitView
        className="bg-[#F7F9FB]"
        deleteKeyCode="Delete"
        selectionOnDrag={true}
        selectionMode={SelectionMode.Partial}
        multiSelectionKeyCode={["MetaKey", "ControlLeft"]}
        panOnDrag={[1, 2]}
      >
        <Controls />
        <MiniMap />
        <Background />
      </ReactFlow>
      
      {/* Campo de busca de componentes com ícones e dropdown */}
      <ComponentSearchDropdown 
        setNodes={setNodes}
        reactFlowInstance={reactFlowInstance}
        onSave={onSave}
        onUndo={onUndo}
        onRedo={onRedo}
        canUndo={canUndo}
        canRedo={canRedo}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        onAddNew={onAddNew}
        autoSaveStatus={autoSaveStatus}
        diagramName={diagramName}
      />
    </div>
  );
});

DiagramCanvas.displayName = 'DiagramCanvas';
